import { MAX_GROUP_IDS } from './types';

/**
 * Input validation for app onboarding.
 *
 * Split out of onboarding.ts so the browser admin console can import the exact same rules and the
 * exact same error messages. That is the whole point: a duplicated regex in the frontend drifts, and
 * the drift shows up as a form that accepts something the API then rejects.
 *
 * Like types.ts, this file must stay free of runtime dependencies - no AWS SDK, no CDK, no zod. It is
 * bundled into a browser build.
 */

export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

/** Lowercase alphanumerics and hyphens, 2-64 chars, must start and end alphanumeric. */
export const APP_NAME_PATTERN = /^[a-z0-9][a-z0-9-]{0,62}[a-z0-9]$/;

export const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Whether `host` is `suffix` or sits beneath it.
 *
 * The dot boundary is the whole point: a bare `endsWith(suffix)` would accept
 * `evilapps.everyhome.org` as being within `apps.everyhome.org`. Used both for the config-time
 * hosted-zone containment check and for the admin API's redirect-URI allowlist, so the subtlety is
 * stated once and tested once.
 */
export function isWithinDomain(host: string, suffix: string): boolean {
  return host === suffix || host.endsWith(`.${suffix}`);
}

/** Trims and validates an app name, returning the trimmed value. */
export function normalizeAppName(appName: string): string {
  const trimmed = appName.trim();
  if (!APP_NAME_PATTERN.test(trimmed)) {
    throw new ValidationError(
      `Invalid app name "${trimmed}": use lowercase letters, digits and hyphens, e.g. "forward-summit"`,
    );
  }
  return trimmed;
}

/**
 * Note the normalization is not identity-preserving: `parsed.toString()` appends a trailing slash to
 * a bare origin, so "https://example.com" is stored as "https://example.com/". Cognito matches
 * redirect_uri as an exact string, so callers that display or re-send these values must use the
 * normalized form rather than what the operator typed.
 */
function normalizeUris(uris: string[], label: string): string[] {
  const normalized = uris.map((raw) => {
    const value = raw.trim();
    let parsed: URL;
    try {
      parsed = new URL(value);
    } catch {
      throw new ValidationError(`${label} "${value}" is not a valid absolute URL`);
    }
    // Cognito permits http only for localhost, and staff callbacks should be TLS regardless.
    const isLocalhost = parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
    if (parsed.protocol !== 'https:' && !(parsed.protocol === 'http:' && isLocalhost)) {
      throw new ValidationError(
        `${label} "${value}" must use https (http is allowed only for localhost)`,
      );
    }
    if (parsed.hash) {
      throw new ValidationError(`${label} "${value}" must not contain a fragment`);
    }
    return parsed.toString();
  });

  return [...new Set(normalized)];
}

export function normalizeRedirectUris(uris: string[]): string[] {
  if (uris.length === 0) {
    throw new ValidationError('At least one redirect URI is required');
  }
  return normalizeUris(uris, 'Redirect URI');
}

/**
 * The same rules as normalizeRedirectUris, with one difference: an empty list is legal.
 *
 * Logout URIs are optional - an app that never calls Cognito's `/logout` needs none, and every
 * registry row written before they existed has none. That asymmetry is deliberate rather than an
 * oversight: an app client with no callback cannot be signed in to at all, so an empty redirect list
 * is always a mistake, while an empty logout list is a supported configuration.
 *
 * Byte-exactness matters here for exactly the reason it does for redirect URIs. Cognito string-matches
 * the `logout_uri` query parameter against the registered LogoutURLs, so what gets registered and
 * what the client sends must be the same normalized string, trailing slash included.
 */
export function normalizeLogoutUris(uris: string[]): string[] {
  return normalizeUris(uris, 'Logout URI');
}

export function normalizeGroupIds(groupIds: string[]): string[] {
  const normalized = groupIds.map((raw) => {
    const value = raw.trim();
    if (!UUID_PATTERN.test(value)) {
      throw new ValidationError(`"${value}" is not a valid Entra group object ID (expected a UUID)`);
    }
    return value.toLowerCase();
  });

  const unique = [...new Set(normalized)];
  if (unique.length > MAX_GROUP_IDS) {
    // Graph's checkMemberGroups caps at 20 per call. Reject here rather than let the trigger deny
    // every login at runtime.
    throw new ValidationError(
      `At most ${MAX_GROUP_IDS} group IDs are supported per app, got ${unique.length}`,
    );
  }
  return unique;
}
